import { createMiddleware } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import * as asyncHooks from "node:async_hooks";
import { CONSINNA_HOSTS, getRoutePrefixForHostname } from "~/config/domains.ts";

/**
 * Per-brand Clerk instance override for the server `clerkMiddleware`.
 *
 * `clerkMiddleware()` is constructed once, but its `loadOptions` reads the
 * `overrides` object **per request**. We exploit that: `brandClerkOptions`
 * exposes `secretKey`/`publishableKey`/`jwtKey` as getters backed by an
 * {@link AsyncLocalStorage}, and {@link brandClerkCaptureMiddleware} (which runs
 * first) populates that store from the request host. The result is that the
 * stock `clerkMiddleware` — and therefore `auth()` and the SSR
 * `clerkInitialState` — transparently use the **clawinstall** Clerk instance on
 * clawinstall hosts, **consinna** on consinna hosts, **orcaloops** on the orca
 * portal, and the default instance everywhere else, with no reimplementation of
 * Clerk internals.
 *
 * A getter returning `undefined` makes `clerkMiddleware` fall back to its
 * environment defaults (`overrides.secretKey || env.SECRET_KEY`), so the default
 * brand and the pre-cutover state (no `*_CLAWINSTALL` vars) behave exactly as
 * before.
 *
 * @since 1.0.0
 */

/**
 * Clerk instance keys captured for the current request.
 *
 * @since 1.0.0
 */
export interface BrandClerkKeys {
  readonly publishableKey: string;
  readonly secretKey: string;
  readonly jwtKey: string;
}

// The `AsyncLocalStorage` instance is created lazily and ONLY on the server —
// the capture middleware and the getters below run server-side. The namespace
// import + lazy `new` keep `node:async_hooks` from breaking the browser bundle:
// the production vite build traverses `start.ts` into the client bundle, where
// `node:async_hooks` is externalized to an empty stub. A static named import
// (`import { AsyncLocalStorage }`) would fail rollup's export check; a namespace
// import does not, and the stub is never executed because `brandClerkStore()` is
// never called in the browser.
let storeSingleton: asyncHooks.AsyncLocalStorage<BrandClerkKeys> | undefined;
const brandClerkStore = (): asyncHooks.AsyncLocalStorage<BrandClerkKeys> => {
  storeSingleton ??= new asyncHooks.AsyncLocalStorage<BrandClerkKeys>();
  return storeSingleton;
};

/**
 * Resolve the server-side Clerk keys for a brand.
 *
 * ClawInstall uses its own instance via the `*_CLAWINSTALL` server vars,
 * Consinna via `*_CONSINNA` and the orca portal via `*_ORCALOOPS` (each a
 * separate user pool); an unset var yields `""` so `clerkMiddleware` falls back
 * to the default environment keys. The default brand contributes no overrides at
 * all. Takes the request host so it can pick Consinna before ClawInstall (a
 * Consinna host is also a ClawInstall host).
 *
 * @since 1.0.0
 */
export const serverBrandClerkKeys = (host: string): BrandClerkKeys => {
  // Consinna is its own Clerk application (separate user pool); its hosts render
  // the ClawInstall product but verify sessions against the Consinna instance.
  // Checked FIRST because a Consinna host is also a ClawInstall host.
  if (isConsinnaHost(host)) {
    return {
      publishableKey: process.env.CLERK_PUBLISHABLE_KEY_CONSINNA ?? "",
      secretKey: process.env.CLERK_SECRET_KEY_CONSINNA ?? "",
      jwtKey: process.env.CLERK_JWT_KEY_CONSINNA ?? "",
    };
  }
  if (isClawInstallHost(host)) {
    return {
      publishableKey: process.env.CLERK_PUBLISHABLE_KEY_CLAWINSTALL ?? "",
      secretKey: process.env.CLERK_SECRET_KEY_CLAWINSTALL ?? "",
      jwtKey: process.env.CLERK_JWT_KEY_CLAWINSTALL ?? "",
    };
  }
  // The orca portal is its own Clerk application. Unlike ClawInstall/Consinna
  // there is no domain-locked key to avoid, so an unset var yielding `""` is the
  // intended pre-cutover state: `clerkMiddleware` falls back to its environment
  // defaults and orcaloops.ai / orca.test keep verifying against the default
  // (development, not domain-locked) instance.
  if (isOrcaloopsHost(host)) {
    return {
      publishableKey: process.env.CLERK_PUBLISHABLE_KEY_ORCALOOPS ?? "",
      secretKey: process.env.CLERK_SECRET_KEY_ORCALOOPS ?? "",
      jwtKey: process.env.CLERK_JWT_KEY_ORCALOOPS ?? "",
    };
  }
  return { publishableKey: "", secretKey: "", jwtKey: "" };
};

/**
 * Getter-backed overrides handed to `clerkMiddleware(brandClerkOptions)`.
 *
 * @since 1.0.0
 */
export const brandClerkOptions = {
  get publishableKey(): string {
    return brandClerkStore().getStore()?.publishableKey ?? "";
  },
  get secretKey(): string {
    return brandClerkStore().getStore()?.secretKey ?? "";
  },
  get jwtKey(): string {
    return brandClerkStore().getStore()?.jwtKey ?? "";
  },
};

/**
 * Whether a request host belongs to the ClawInstall brand. Mirrors the
 * detection in {@link file://./hostname.ts}.
 *
 * @since 1.0.0
 */
export const isClawInstallHost = (host: string): boolean => {
  const hostname = host.split(":")[0].toLowerCase();
  return getRoutePrefixForHostname(hostname) === "/clawinstall" || import.meta.env.VITE_FORCE_CLAWINSTALL === "true";
};

/**
 * Whether a request host is a Consinna front door. Consinna authenticates
 * against its own Clerk instance, so this takes precedence over
 * {@link isClawInstallHost} in {@link serverBrandClerkKeys}.
 *
 * @since 1.0.0
 */
export const isConsinnaHost = (host: string): boolean => {
  const hostname = host.split(":")[0].toLowerCase();
  return CONSINNA_HOSTS.includes(hostname);
};

/**
 * Whether a request host is an orca portal front door. Resolved through the ONE
 * domain registry (never a substring detector), so it covers `orcaloops.ai`,
 * `www.orcaloops.ai`, every `<x>.orcaloops.ai`, and the dev orca hosts
 * (`orca.test`, `orca.ngrok.io`, `orca.theinfinityguides.com`). Mirrors
 * {@link isClawInstallHost}, including the forced-brand dev override.
 *
 * @since 1.0.0
 */
export const isOrcaloopsHost = (host: string): boolean => {
  const hostname = host.split(":")[0].toLowerCase();
  return getRoutePrefixForHostname(hostname) === "/orca" || import.meta.env.VITE_FORCE_ORCA === "true";
};

/**
 * Request middleware that captures the brand's Clerk keys into the store for
 * the rest of the request. Must run BEFORE `clerkMiddleware`.
 *
 * @since 1.0.0
 */
export const brandClerkCaptureMiddleware = createMiddleware().server((ctx) => {
  const headers = getRequestHeaders();
  const host = headers?.get("x-forwarded-host") || headers?.get("host") || "";
  return brandClerkStore().run(serverBrandClerkKeys(host), () => ctx.next());
});
